import React, { useMemo, useState } from "react";
import { Alert, Button, Checkbox, Empty, Input, Space, Tag, Tooltip } from "antd";
import {
  buildPermissionModules,
  matchesPermissionSearch,
  SENSITIVITY_LABELS,
} from "./rolesPermissionsUtils";

const sensitivityColor = {
  NORMAL: "default",
  SENSITIVE: "blue",
  HIGH_RISK: "orange",
  SYSTEM_ONLY: "red",
};

function SensitivityTag({ value }) {
  const label = SENSITIVITY_LABELS[value] || value || "NORMAL";
  const content = (
    <Tag color={sensitivityColor[label] || "default"} className="rp-sensitivity-tag">
      {label === "HIGH_RISK" ? "! " : ""}
      {label}
    </Tag>
  );

  if (label !== "HIGH_RISK") return content;
  return <Tooltip title="Permiso de alto riesgo">{content}</Tooltip>;
}

export default function RolePermissionsMatrix({
  catalog,
  selectedIds,
  readOnly,
  dirty,
  saving,
  error,
  onChange,
  onSave,
  // RBAC-CUSTOM-ROLES-03B seccion L: al reutilizar esta matriz DENTRO del
  // formulario "Nuevo rol", el guardado real ocurre via el boton "Guardar"
  // del modal (POST /roles) -- el boton interno "Guardar permisos" (que
  // llama PUT /roles/:idrol/permissions, inexistente para un rol que aun no
  // existe) debe ocultarse.
  hideSaveButton = false,
}) {
  const [search, setSearch] = useState("");
  const modules = useMemo(() => buildPermissionModules(catalog), [catalog]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const visibleModules = modules.filter((module) => matchesPermissionSearch(module, search));

  const setModuleIds = (module, checked) => {
    const next = new Set(selectedSet);
    module.permissions.forEach((permission) => {
      if (checked) {
        next.add(permission.idpermiso);
      } else {
        next.delete(permission.idpermiso);
      }
    });
    onChange([...next]);
  };

  const togglePermission = (permission, checked) => {
    const next = new Set(selectedSet);
    if (checked) {
      next.add(permission.idpermiso);
    } else {
      next.delete(permission.idpermiso);
    }
    onChange([...next]);
  };

  if (!catalog.length) {
    return <Empty description="No hay permisos disponibles" />;
  }

  return (
    <div className="rp-matrix">
      {error && <Alert className="mb-3" type="error" message={error} showIcon />}

      <div className="rp-toolbar">
        <Input.Search
          allowClear
          placeholder="Buscar funcionalidad"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ maxWidth: 360 }}
        />
        {!readOnly && !hideSaveButton && (
          <Button type="primary" disabled={!dirty} loading={saving} onClick={onSave}>
            Guardar permisos
          </Button>
        )}
      </div>

      {!visibleModules.length ? (
        <Empty description="Sin resultados" />
      ) : (
        <div className="rp-module-list">
          {visibleModules.map((module) => {
            const moduleIds = module.permissions.map((permission) => permission.idpermiso);
            const checkedCount = moduleIds.filter((id) => selectedSet.has(id)).length;
            const allChecked = checkedCount === moduleIds.length && moduleIds.length > 0;
            const indeterminate = checkedCount > 0 && checkedCount < moduleIds.length;

            return (
              <section className="rp-module" key={module.key}>
                <div className="rp-module-header">
                  <div>
                    <h6>{module.label}</h6>
                    <span>{checkedCount}/{moduleIds.length}</span>
                  </div>
                  {!readOnly && (
                    <Space>
                      <Checkbox
                        checked={allChecked}
                        indeterminate={indeterminate}
                        onChange={(event) => setModuleIds(module, event.target.checked)}
                      >
                        Seleccionar todos
                      </Checkbox>
                      <Button size="small" onClick={() => setModuleIds(module, false)}>
                        Limpiar módulo
                      </Button>
                    </Space>
                  )}
                </div>

                <div className="rp-permission-grid">
                  {module.permissions.map((permission) => (
                    <label className="rp-permission-cell" key={permission.idpermiso}>
                      <Checkbox
                        checked={selectedSet.has(permission.idpermiso)}
                        disabled={readOnly}
                        onChange={(event) => togglePermission(permission, event.target.checked)}
                      />
                      <div>
                        <strong>{permission.actionLabel}</strong>
                        <span>{permission.nombre}</span>
                        {permission.descripcion && <small>{permission.descripcion}</small>}
                      </div>
                      <SensitivityTag value={permission.sensibilidad} />
                    </label>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
